import { useCallback, useEffect, useState } from "react";
import { usePlaidLink, type PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import { createPlaidLinkToken, exchangePlaidPublicToken } from "../api/client";
import { BUYER_FUNDING_FLOW_PLAID_LIMITATION } from "../flows/buyerFunding/types";
import type { BuyerFundingFlowPlaidEvidence } from "../flows/buyerFunding/types";
import { PlaidIntroInterstitial } from "./PlaidIntroInterstitial";

type Props = {
  fileCode: string;
  buyerName: string;
  onConnected: (evidence: BuyerFundingFlowPlaidEvidence) => void;
  onSkip: () => void;
  onSimulate?: () => void;
};

export function PlaidConnectPanel({ fileCode, buyerName, onConnected, onSkip, onSimulate }: Props) {
  const staticToken = import.meta.env.VITE_PLAID_LINK_TOKEN as string | undefined;
  const [serverLinkToken, setServerLinkToken] = useState<string | null>(null);
  const [linkError, setLinkError] = useState("");

  useEffect(() => {
    if (staticToken && staticToken.length > 10) return;
    void createPlaidLinkToken(fileCode)
      .then((result) => {
        setServerLinkToken(result.linkToken);
        setLinkError("");
      })
      .catch((error) => {
        setServerLinkToken(null);
        setLinkError(error instanceof Error ? error.message : "Could not start Plaid.");
      });
  }, [fileCode, staticToken]);

  const linkToken = staticToken && staticToken.length > 10 ? staticToken : serverLinkToken;
  const plaidEnabled = Boolean(linkToken && linkToken.length > 10);

  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        const evidence = await exchangePlaidPublicToken({
          fileCode,
          publicToken,
          buyerName
        });
        onConnected(evidence);
        return;
      } catch {
        // Fall back to client metadata when exchange API unavailable (e.g. Vite-only).
      }
      const account = metadata.accounts?.[0];
      onConnected({
        mode: "live",
        connectedAt: new Date().toISOString(),
        ownerName: account?.name || buyerName,
        institutionName: metadata.institution?.name ?? undefined,
        accountLast4: account?.mask ?? undefined,
        limitation: BUYER_FUNDING_FLOW_PLAID_LIMITATION
      });
    },
    [buyerName, fileCode, onConnected]
  );

  if (plaidEnabled) {
    return (
      <PlaidLinkLive
        linkToken={linkToken!}
        linkError={linkError}
        onSuccess={handlePlaidSuccess}
        onSkip={onSkip}
      />
    );
  }

  return <PlaidConnectDev onSkip={onSkip} onSimulate={onSimulate} linkError={linkError} />;
}

function PlaidConnectDev({
  onSkip,
  onSimulate,
  linkError
}: {
  onSkip: () => void;
  onSimulate?: () => void;
  linkError?: string;
}) {
  const [introDone, setIntroDone] = useState(false);

  if (!introDone) {
    return (
      <PlaidIntroInterstitial
        onContinue={() => setIntroDone(true)}
        onSkip={onSkip}
        continueLabel="Continue"
      />
    );
  }

  return (
    <section className="panel" data-testid="plaid-connect-panel">
      <p className="label">Before you pay</p>
      <h2 className="mt-1 text-xl font-bold">Verify your bank with Plaid</h2>
      <p className="mt-2 text-sm text-[#596158]">
        Plaid verifies your bank details before you send funds from your bank app or branch. Veto records source-account
        evidence for your office — it does not move money or approve your payment.
      </p>
      <p className="notice mt-3 text-sm">
        {linkError
          ? linkError
          : "Plaid Link is not configured in this environment yet. You can continue without connecting, or use simulate in development."}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {onSimulate ? (
          <button className="button-primary" type="button" data-testid="plaid-simulate" onClick={onSimulate}>
            Simulate bank connect (dev)
          </button>
        ) : null}
        <button className="button-secondary" type="button" data-testid="plaid-skip" onClick={onSkip}>
          Skip bank connection
        </button>
      </div>
    </section>
  );
}

function PlaidLinkLive({
  linkToken,
  linkError,
  onSuccess,
  onSkip
}: {
  linkToken: string;
  linkError?: string;
  onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => void | Promise<void>;
  onSkip: () => void;
}) {
  const [introDone, setIntroDone] = useState(false);
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken, metadata) => {
      void onSuccess(publicToken, metadata);
    }
  });

  useEffect(() => {
    if (introDone && ready) {
      open();
    }
  }, [introDone, ready, open]);

  if (!introDone) {
    return (
      <PlaidIntroInterstitial
        onContinue={() => setIntroDone(true)}
        onSkip={onSkip}
        continueLabel={ready ? "Continue to Plaid" : "Loading Plaid…"}
        continueDisabled={!ready}
      />
    );
  }

  return (
    <section className="panel" data-testid="plaid-connect-panel">
      <p className="label">Before you pay</p>
      <h2 className="mt-1 text-xl font-bold">Opening Plaid…</h2>
      <p className="mt-2 text-sm text-[#596158]">
        Complete bank connection in the Plaid window. Auth and Identity are recorded server-side when configured.{" "}
        {BUYER_FUNDING_FLOW_PLAID_LIMITATION}
      </p>
      {linkError ? <p className="notice mt-3 text-sm">{linkError}</p> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="button-primary"
          type="button"
          data-testid="plaid-open"
          disabled={!ready}
          onClick={() => open()}
        >
          Open Plaid again
        </button>
        <button className="button-secondary" type="button" data-testid="plaid-skip" onClick={onSkip}>
          Skip bank connection
        </button>
      </div>
    </section>
  );
}
